/**
 * The players' numbers (spec 02 §3), each from the rebb64 routine that uses it. Coordinates are the
 * C64's VIC sprite registers (see types.ts); "Y on the grid" means Y = $2D + 8 * row.
 */

/** Start / respawn spot: X by player (D_A735), Y (check_player_state $04E6), frame by player (D_A737). */
export const START_X: readonly [number, number] = [0x2c, 0xec];
export const START_Y = 0xdd;
export const START_FRAME: readonly [number, number] = [0x00, 0x04];

/** X limits in the air (D_2483 drift, D_25F1 steering); on the ground the side walls stop at the same. */
export const X_MIN = 0x24;
export const X_MAX = 0xf4;

/** Walk: 2 px per I-tick (D_226B / D_22C3); the walk frame toggles every 4 ticks (L2294). */
export const WALK_STEP = 2;
export const WALK_ANIM_TICKS = 4;
/** Standing with no input: the frame toggles every 7 ticks (D_2162 L21EB). */
export const IDLE_ANIM_TICKS = 7;
/** In the air, drift runs every tick; steering and fire every 2nd tick (L24FA, L_25E2). */
export const AIR_CHECK_TICKS = 2;

/** Jump (D_222B, D_23EA): 15 rise steps from table $ACCD read backwards, then the same forwards. */
export const JUMP_STEPS = 15;
export const JUMP_TABLE: readonly number[] = [0, 1, 1, 2, 2, 2, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4];
/** The descent step at which the jump is over (L2423 `cmp #$10`). */
export const DESCENT_END = 16;

/** Grid base of the landing tests and snaps: standing Y = $2D + 8 * row. */
export const GRID_Y = 0x2d;
/** Below this Y the jump descent (L242A) and the fall that ends a jump (D_EB3F) skip the landing test. */
export const LAND_MIN_Y_JUMP = 0x1f;
/** Below this Y a fall (D_257C) skips the landing test. */
export const LAND_MIN_Y_FALL = 0x26;

/** Fall: 2 px per I-tick (D_257C); Y $F5 wraps to $15. */
export const FALL_STEP = 2;
export const WRAP_BOTTOM = 0xf5;
export const WRAP_TOP = 0x15;
/** Jump wraps: rising below Y 5 adds $F0 (D_23EA); descending at or past $F5 takes $F0 away (L242A). */
export const RISE_WRAP_BELOW = 0x05;
export const WRAP_SPAN = 0xf0;

/** Blow (D_22E8, D_2301): 6 animation ticks after the press; the bubble on the tick the timer reads 4. */
export const BLOW_TICKS = 6;
export const BLOW_SPAWN_TICK = 4;
/** Blow frames by timer value (table $ACC4), added to 0 (facing right) or 2 (facing left). */
export const BLOW_FRAMES: readonly number[] = [0x00, 0x08, 0x08, 0x09, 0x09, 0x08, 0x08];
/** Main-loop passes before the next blow (D_A77B via D_A824): 8, or 3 with the yellow candy. */
export const BLOW_COOLDOWN = 8;
export const BLOW_COOLDOWN_CANDY = 3;

/** Bounce (D_2162 L217C): |Bx - Px| < $0E and 0 <= By - Py < $14; scores 1 (10 displayed, D_7C21). */
export const BOUNCE_DX = 0x0e;
export const BOUNCE_DY = 0x14;
export const BOUNCE_SCORE = 1;
/** Ring scores (D_7C21 from D_2483, L22B4, L23D7): 1 per tick airborne / walking / bubble. */
export const RING_SCORE = 1;

/** Push (D_0AAB, per main-loop pass): 4 px; reach $14 to the left, $12 to the right, |dy| < $0E. */
export const PUSH_STEP = 4;
export const PUSH_REACH_LEFT = 0x14;
export const PUSH_REACH_RIGHT = 0x12;
export const PUSH_DY = 0x0e;

/** Death animation (spawn_animation $28A5): timer from $20 down to $C8 (88 ticks), falling 3 px while >= 0. */
export const DEATH_TIMER = 0x20;
export const DEATH_TIMER_END = 0xc8;
export const DEATH_FALL = 3;
/** Respawn invulnerability, I-ticks (D_8688 = $4A in check_player_state). */
export const INVULNERABLE_TICKS = 0x4a;
/** A join or continue sets the lives counter to 4; the respawn at once takes one (check_join_game). */
export const JOIN_LIVES = 4;
/** Monsters still in the level for a death to calm them all (check_player_state `ZP_4A >= 2`). */
export const CALM_MIN_MONSTERS = 2;

/** Level index $5B (level 92): D_2519 lets a jump end standing at Y $DD instead of falling. */
export const LEVEL_92_INDEX = 0x5b;
